import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Robot, PaperPlaneRight, Plus, Trash, CircleNotch, ChatCircleDots, Sparkle,
  Paperclip, ArrowLeft, X, WhatsappLogo, ClockCounterClockwise,
} from '@phosphor-icons/react';

const SUGGESTIONS = [
  '¿Cuánto vendió cada vendedor este mes?',
  '¿Qué OTs están pendientes de entrega?',
  '¿Qué instalaciones hay programadas esta semana?',
  'Busca el precio y existencia de bisagras en HarpERP',
  '¿Cómo van las metas de los vendedores?',
];

export default function ChatIA() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo imágenes'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      const url = r.data.url || r.data.cdn_url || r.data.secure_url;
      if (url) { setPendingImages((prev) => [...prev, url]); toast.success('Imagen adjuntada — se incluirá en la cotización'); }
      else toast.error('No se obtuvo URL de la imagen');
    } catch (er) { toast.error(er?.response?.data?.detail || 'Error subiendo imagen'); }
    setUploading(false);
  };

  const sendCotWa = async (c) => {
    const phone = window.prompt('Número de WhatsApp del cliente (8 dígitos o con código de país):');
    if (!phone) return;
    let incluirImagenes = true;
    if (c.imagenes > 0) {
      incluirImagenes = window.confirm('¿Incluir las imágenes de referencia en el PDF?\n\nAceptar = con imágenes · Cancelar = sin imágenes');
    }
    toast.info('Enviando cotización por WhatsApp…');
    try {
      const r = await api.post(`/cotizaciones/${c.id}/enviar-whatsapp`, { phone, incluir_imagenes: incluirImagenes }, { timeout: 120000 });
      toast.success(`✓ Cotización ${c.numero} enviada al ${r.data.phone}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error enviando por WhatsApp'); }
  };

  const fetchSessions = async () => {
    try {
      const r = await api.get('/chat-ia/sessions');
      setSessions(r.data.sessions || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error(e.response.data.detail);
    }
  };

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openSession = async (sid) => {
    setActiveSession(sid);
    setShowSessions(false);
    try {
      const r = await api.get(`/chat-ia/sessions/${sid}/messages`);
      setMessages(r.data.messages || []);
    } catch { toast.error('Error cargando conversación'); }
  };

  const newChat = () => { setActiveSession(null); setMessages([]); setShowSessions(false); };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    await api.delete(`/chat-ia/sessions/${sid}`);
    if (sid === activeSession) newChat();
    fetchSessions();
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    const images = pendingImages;
    setInput('');
    setPendingImages([]);
    setMessages((prev) => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '', _streaming: true }]);
    setStreaming(true);
    try {
      const res = await fetch(`${api.defaults.baseURL}/chat-ia/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, session_id: activeSession, images }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sid = activeSession;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          if (ev.type === 'session') { sid = ev.session_id; }
          else if (ev.type === 'delta') {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + ev.content };
              return next;
            });
          } else if (ev.type === 'cotizacion') {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], cotizacion: { id: ev.id, numero: ev.numero, total: ev.total, cliente: ev.cliente, empresa: ev.empresa, imagenes: ev.imagenes || 0 } };
              return next;
            });
          } else if (ev.type === 'documento') {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], documento: { url: ev.url, nombre: ev.nombre, formato: ev.formato } };
              return next;
            });
          } else if (ev.type === 'ocr') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = next.length - 2;
              if (idx >= 0 && next[idx].role === 'user') next[idx] = { ...next[idx], content: next[idx].content + ev.note };
              return next;
            });
          } else if (ev.type === 'error') {
            toast.error(ev.detail || 'Error en la respuesta de IA');
          }
        }
      }
      setMessages((prev) => prev.map((m) => ({ ...m, _streaming: false })));
      if (!activeSession && sid) setActiveSession(sid);
      fetchSessions();
    } catch (e) {
      toast.error(e.message || 'Error enviando mensaje');
      setMessages((prev) => prev.filter((m) => !m._streaming || m.content));
    } finally {
      setStreaming(false);
    }
  };

  const downloadCot = async (c, conImagenes = true) => {
    try {
      const res = await api.get(`/cotizaciones/${c.id}/pdf?imagenes=${conImagenes ? 1 : 0}`, { responseType: 'blob', timeout: 60000 });
      const u = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = u;
      a.download = `Cotizacion_${c.numero}${conImagenes ? '' : '_sin_imagenes'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
    } catch { toast.error('Error descargando el PDF'); }
  };

  const renderContent = (c) => c.split('\n').map((line, i) => {
    const bold = line.split(/\*\*(.+?)\*\*/g).map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part));
    return <p key={i} className={line.startsWith('- ') || line.startsWith('• ') ? 'pl-3' : ''}>{bold}</p>;
  });

  const sessionsPanel = (
    <>
      <div className="p-3">
        <button onClick={newChat} className="w-full flex items-center justify-center gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2.5 rounded-[10px]" data-testid="btn-new-chat">
          <Plus size={14} weight="bold" /> Nueva conversación
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
        {sessions.map((s) => (
          <div key={s.id} onClick={() => openSession(s.id)}
            className={`group flex items-center gap-2 px-3 py-2.5 rounded-[10px] cursor-pointer text-xs ${s.id === activeSession ? 'bg-[var(--primary-soft)] text-[var(--primary)] font-bold' : 'hover:bg-[var(--surface-muted)] text-[var(--text-muted)]'}`}
            data-testid={`session-${s.id}`}>
            <ChatCircleDots size={14} className="shrink-0" />
            <span className="flex-1 truncate">{s.title}</span>
            <button onClick={(e) => deleteSession(s.id, e)} className="md:opacity-0 md:group-hover:opacity-100 text-red-500 p-0.5" data-testid={`delete-session-${s.id}`}>
              <Trash size={13} />
            </button>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-[10px] text-[var(--text-faint)] text-center pt-6">Sin conversaciones aún</p>}
      </div>
    </>
  );

  return (
    <div className="h-full flex" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="chat-ia-page">
      {/* Sessions sidebar (desktop) */}
      <div className="w-64 border-r border-[var(--border)] flex-col shrink-0 hidden md:flex">
        {sessionsPanel}
      </div>

      {/* Sessions drawer (mobile) */}
      {showSessions && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="sessions-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSessions(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[80%] max-w-[300px] bg-[var(--surface)] border-r border-[var(--border)] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-3 pt-3">
              <p className="text-xs font-black uppercase text-[var(--text-muted)]">Conversaciones</p>
              <button onClick={() => setShowSessions(false)} className="p-1.5 hover:bg-[var(--surface-muted)] rounded" data-testid="close-sessions-drawer">
                <X size={16} />
              </button>
            </div>
            {sessionsPanel}
          </div>
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 md:px-5 py-3 border-b border-[var(--border)] flex items-center gap-2 shrink-0">
          <button onClick={() => setShowSessions(true)}
            className="md:hidden p-2 -ml-1 border border-[var(--border)] rounded-[10px] text-[var(--text-muted)] hover:text-[var(--text)] relative"
            title="Ver conversaciones" data-testid="btn-open-sessions">
            <ClockCounterClockwise size={18} weight="bold" />
            {sessions.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[var(--primary)] text-white text-[9px] font-black rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{sessions.length}</span>
            )}
          </button>
          <Robot size={22} weight="fill" className="text-[var(--primary)]" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-black heading-font">Chat IA</h1>
            <p className="text-[10px] text-[var(--text-muted)] truncate">Conectado a tus datos: ventas, metas, OTs, programación y HarpERP</p>
          </div>
          <button onClick={newChat} className="md:hidden p-2 border border-[var(--border)] rounded-[10px] text-[var(--text-muted)] hover:text-[var(--text)]"
            title="Nueva conversación" data-testid="btn-new-chat-mobile">
            <Plus size={18} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 ? (
            <div className="max-w-xl mx-auto text-center pt-10">
              <Sparkle size={36} weight="duotone" className="mx-auto mb-3 text-[var(--primary)]" />
              <h2 className="text-lg font-black mb-1">¿En qué te ayudo hoy?</h2>
              <p className="text-xs text-[var(--text-muted)] mb-5">Pregúntame sobre ventas, metas, producción, programación o el catálogo HarpERP.</p>
              <div className="space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="w-full text-left text-xs px-4 py-2.5 border border-[var(--border)] rounded-[10px] hover:bg-[var(--surface-muted)] hover:border-[var(--primary)] transition-colors" data-testid="suggestion-btn">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-[14px] text-sm leading-relaxed space-y-1 ${m.role === 'user' ? 'bg-[var(--primary)] text-white' : 'bg-[var(--surface)] border border-[var(--border)]'}`}>
                  {m.content ? renderContent(m.content) : (m._streaming && <CircleNotch size={16} className="animate-spin" />)}
                  {m.documento && (
                    <div className="mt-2">
                      <a href={m.documento.url} target="_blank" rel="noreferrer" download={m.documento.nombre}
                        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded-[8px]"
                        data-testid="btn-download-documento">
                        {m.documento.formato === 'excel' ? '📊' : '📄'} Descargar {m.documento.formato === 'excel' ? 'Excel' : 'PDF'} · {m.documento.nombre}
                      </a>
                    </div>
                  )}
                  {m.cotizacion && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button onClick={() => downloadCot(m.cotizacion, true)}
                        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-[8px]"
                        data-testid={`btn-download-cot-${m.cotizacion.id}`}>
                        📄 {m.cotizacion.imagenes > 0 ? 'PDF con imágenes' : 'Descargar PDF'} · {m.cotizacion.numero}
                      </button>
                      {m.cotizacion.imagenes > 0 && (
                        <button onClick={() => downloadCot(m.cotizacion, false)}
                          className="inline-flex items-center gap-1.5 bg-slate-600 hover:bg-slate-700 text-white text-xs font-bold px-3 py-2 rounded-[8px]"
                          data-testid={`btn-download-cot-sin-img-${m.cotizacion.id}`}>
                          📄 PDF sin imágenes
                        </button>
                      )}
                      <button onClick={() => sendCotWa(m.cotizacion)}
                        className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20B858] text-white text-xs font-bold px-3 py-2 rounded-[8px]"
                        data-testid={`btn-wa-cot-${m.cotizacion.id}`}>
                        <WhatsappLogo size={14} weight="fill" /> Enviar por WhatsApp
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 pb-24 md:pb-6 border-t border-[var(--border)] shrink-0">
          {pendingImages.length > 0 && (
            <div className="max-w-3xl mx-auto flex gap-2 mb-2 flex-wrap" data-testid="pending-images">
              {pendingImages.map((u, i) => (
                <div key={i} className="relative">
                  <img src={u} alt="adjunto" className="h-14 w-14 object-cover rounded-[8px] border border-[var(--border)]" />
                  <button onClick={() => setPendingImages((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-image-${i}`}>
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
              <span className="text-[10px] text-[var(--text-muted)] self-center">Se incluirán en la cotización</span>
            </div>
          )}
          <div className="max-w-3xl mx-auto flex gap-2 md:pr-0 pr-2 items-end">
            <button onClick={() => navigate(-1)} title="Regresar"
              className="p-3 border border-[var(--border)] rounded-[12px] text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)]"
              data-testid="chat-ia-back">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFilePick} data-testid="chat-ia-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Adjuntar imagen para la cotización"
              className="p-3 border border-[var(--border)] rounded-[12px] text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)] disabled:opacity-50"
              data-testid="chat-ia-attach">
              {uploading ? <CircleNotch size={18} className="animate-spin" /> : <Paperclip size={18} weight="bold" />}
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={2}
              placeholder="Escribe tu mensaje… (Enter = nueva línea, envía con el botón)"
              className="flex-1 resize-none bg-[var(--surface)] border border-[var(--border)] rounded-[12px] px-4 py-3 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="chat-ia-input"
            />
            <button onClick={() => send()} disabled={streaming || !input.trim()}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 rounded-[12px] disabled:opacity-40"
              data-testid="chat-ia-send">
              {streaming ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneRight size={18} weight="fill" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
